import React, { useEffect, useRef, useState } from "react";
import { usePolling } from "../lib/usePolling";
import { loadValueOfDay, resetValueOfDay } from "../lib/valueOfDay";
import { isValueEnabled } from "../lib/features";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import api from "../lib/api";
import { Header } from "../components/Header";
import { MatchCard } from "../components/MatchCard";
import { FollowBell } from "../components/FollowBell";
import { TeamBadge } from "../components/TeamBadge";
import { kickoffTimeTz, kickoffDate } from "../lib/datetime";
import { useAuth } from "../context/AuthContext";
import { useSport } from "../context/SportContext";
import { bandColor, outcomeColor } from "../lib/perf";
import { Footer } from "../components/Footer";
import { isPro } from "../components/ProGate";
import { LotteryBanner } from "../components/LotteryBanner";
import { LaunchCountdown } from "../components/LaunchCountdown";
import { toast } from "sonner";
import { TrendUp, Fire, ArrowRight, Lightning, SoccerBall, TennisBall, Basketball, Sparkle, Check, X, Crown, CaretRight, Gift, Scales, LockKey, Broadcast, PushPin, Trophy, Bookmarks, ChartLineUp } from "@phosphor-icons/react";
import { useHighConfidence } from "../lib/useHighConfidence";
import { isNative } from "../lib/native";

const SPORT_ICON = { foot: SoccerBall, tennis: TennisBall, basket: Basketball };

const Section = ({ title, children, action }) => (
  <section className="mt-8">
    <div className="flex items-center justify-between px-5 mb-3">
      <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

function FeaturedMatch({ m }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="px-5">
      <div
        data-testid="featured-match"
        onClick={() => navigate(m.source === "live" ? `/live/${m.match_id}` : `/match/${m.match_id}`)}
        className="relative rounded-2xl overflow-hidden cursor-pointer border border-white/10 fade-up"
      >
        <img src={m.background} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0A0A0A 12%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,0.4))" }} />
        <div className="relative p-5 pt-6">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">{t("home.featured_label", { competition: m.competition })}</span>
          <div className="flex items-center justify-center gap-6 my-5">
            <div className="flex flex-col items-center gap-2">
              <TeamBadge team={m.home} size={58} />
              <span className="text-sm font-bold text-center leading-tight">{m.home.name}</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="font-numeral font-black text-2xl text-white/40">{t("home.vs")}</span>
              <span className="text-[11px] text-white/60 mt-1 text-center leading-tight" data-testid="featured-kickoff">
                {kickoffDate(m, { weekday: "short", day: "2-digit", month: "short" })}
                {kickoffTimeTz(m) ? <> · {kickoffTimeTz(m)}</> : null}
              </span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <TeamBadge team={m.away} size={58} />
              <span className="text-sm font-bold text-center leading-tight">{m.away.name}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {m.key_stats?.map((k, i) => (
              <div key={i} className="bg-black/40 backdrop-blur rounded-xl p-2.5 text-center border border-white/5">
                <div className="font-numeral font-black text-lg" style={{ color: k.band ? bandColor(k.band) : "#fff" }}>{k.value}</div>
                <div className="text-[9px] font-semibold text-white/50 uppercase tracking-wide mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
          <button
            data-testid="analyze-cta"
            className="w-full h-11 rounded-xl bg-gold text-black font-bold text-sm flex items-center justify-center gap-2 glow-gold"
          >
            {t("home.analyze_match")} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TrendCard({ t }) {
  const navigate = useNavigate();
  const Icon = t.type === "player" ? Fire : t.type === "goals" ? Lightning : TrendUp;
  return (
    <button
      data-testid={`trend-${t.trend_id}`}
      onClick={() => t.team_id && navigate(`/club/${t.team_id}`)}
      className="hm-card p-4 min-w-[220px] snap-start text-left"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} weight="fill" color={bandColor(t.band)} />
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">{t.type}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className="font-numeral font-black text-3xl" style={{ color: bandColor(t.band) }}>{t.value}</span>
        <span className="text-xs text-white/50 mb-1.5">{t.unit}</span>
      </div>
      <p className="text-sm font-bold mt-1">{t.title}</p>
      <p className="text-xs text-white/50 mt-1 leading-snug">{t.detail}</p>
    </button>
  );
}

const BASKET_PREVIEW = [
  { name: "Lakers", abbr: "lal" },
  { name: "Celtics", abbr: "bos" },
  { name: "Warriors", abbr: "gs" },
  { name: "Bulls", abbr: "chi" },
  { name: "Heat", abbr: "mia" },
  { name: "Knicks", abbr: "ny" },
];

function SportComingSoon({ meta }) {
  const { t } = useTranslation();
  const Icon = SPORT_ICON[meta.key];
  const sportName = t(`sports.${meta.key}`);
  return (
    <div className="px-5 mt-10" data-testid="sport-coming-soon">
      <div className="hm-card p-8 flex flex-col items-center text-center fade-up"
        style={{ borderColor: `${meta.color}44`, boxShadow: `0 0 26px ${meta.color}18` }}>
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4"
          style={{ background: `${meta.color}18`, border: `1px solid ${meta.color}66` }}>
          <Icon size={40} weight="duotone" color={meta.color} />
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight" style={{ color: meta.color }}>
          {t("home.coming_soon_title", { sport: sportName })}
        </h2>
        <p className="text-sm text-white/55 mt-3 max-w-xs leading-relaxed">
          {t("home.coming_soon_desc", { sport: sportName.toLowerCase() })}
        </p>

        {meta.key === "basket" && (
          <div className="mt-6 w-full" data-testid="basket-preview">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 mb-3">{t("home.teams_preview")}</p>
            <div className="grid grid-cols-3 gap-3">
              {BASKET_PREVIEW.map((t2) => (
                <div key={t2.abbr} className="flex flex-col items-center gap-1.5">
                  <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center p-2">
                    <img src={`https://a.espncdn.com/i/teamlogos/nba/500/${t2.abbr}.png`} alt={t2.name}
                      className="w-full h-full object-contain" />
                  </div>
                  <span className="text-[10px] text-white/60">{t2.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <span className="mt-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold"
          style={{ background: `${meta.color}18`, color: meta.color }}>
          <Sparkle size={13} weight="fill" /> {t("home.in_preparation")}
        </span>
      </div>
    </div>
  );
}

function PredCard({ p, sport = "foot", pro = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hc = useHighConfidence();
  const c = bandColor(p.band);
  const showHc = p.band === "excellent" && hc && hc.sample >= 10 && hc.accuracy_pct != null;
  const kIso = p.kickoff || p.commence_time || null;
  const kTime = kickoffTimeTz({ kickoff: kIso });
  const open = () => {
    if (p.match_id) navigate(`/match/${p.match_id}`, { state: { pred: p } });
    else navigate(`/prediction/${sport}/${encodeURIComponent(p.home.name)}/${encodeURIComponent(p.away.name)}`, { state: { pred: p } });
  };
  return (
    <button
      data-testid={`pred-card-${p.home.short}-${p.away.short}`}
      onClick={open}
      className="hm-card p-4 min-w-[250px] snap-start text-left"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gold/80 truncate max-w-[45%]">{p.competition || "—"}</span>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-numeral font-black text-lg" style={{ color: c }}>{p.hitmax_score}</span>
          <FollowBell sport={sport} size={14}
            match={{ home: p.home, away: p.away, competition: p.competition, tournament: p.competition, match_id: p.match_id }} />
        </div>
      </div>
      {kIso && kTime && (
        <p className="text-[10px] text-white/45 -mt-2 mb-2" data-testid={`pred-kickoff-${p.home.short}-${p.away.short}`}>
          {kickoffDate({ kickoff: kIso }, { weekday: "short", day: "2-digit", month: "short" })} · {kTime}
        </p>
      )}
      {sport === "foot" && p.sheet_available === false && (
        <div
          data-testid={`sheet-unavailable-${p.home.short}-${p.away.short}`}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 mb-2"
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)" }}
          title="Match non programmé prochainement : pronostic disponible, fiche détaillée indisponible."
        >
          <span className="text-[10px] font-bold tracking-wide text-white/55">FICHE INDISPO</span>
        </div>
      )}
      {isValueEnabled() && typeof p.market_edge === "number" && p.market_edge >= 3 && (!["basket", "tennis"].includes(sport) || pro) && (
        <div
          data-testid={`value-badge-${p.home.short}-${p.away.short}`}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 mb-2"
          style={{ background: "rgba(0,230,118,0.14)", border: "1px solid rgba(0,230,118,0.5)" }}
        >
          <Sparkle size={11} weight="fill" color="#00E676" />
          <span className="text-[10px] font-black tracking-wide" style={{ color: "#00E676" }}>VALUE +{p.market_edge}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <TeamBadge team={p.home} size={34} />
          <span className="text-[11px] font-semibold w-full text-center leading-tight">{p.home.name}</span>
          {p.home.rank ? <span className="text-[9px] font-bold text-gold/80" data-testid="pred-rank-home">ATP/WTA #{p.home.rank}</span> : null}
        </div>
        <span className="font-numeral font-black text-white/30 text-sm">VS</span>
        <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
          <TeamBadge team={p.away} size={34} />
          <span className="text-[11px] font-semibold w-full text-center leading-tight">{p.away.name}</span>
          {p.away.rank ? <span className="text-[9px] font-bold text-gold/80" data-testid="pred-rank-away">ATP/WTA #{p.away.rank}</span> : null}
        </div>
      </div>
      <div className="mt-3 rounded-lg px-2.5 py-1.5 flex items-center justify-between" style={{ background: `${c}14`, border: `1px solid ${c}44` }}>
        <span className="text-[11px] font-bold truncate" style={{ color: c }}>{p.recommendation?.text}</span>
        <span className="text-[11px] font-numeral font-bold" style={{ color: c }}>{p.recommendation?.prob}%</span>
      </div>
      {showHc && (
        <span
          role="button"
          tabIndex={0}
          data-testid={`hc-card-badge-${p.home.short}-${p.away.short}`}
          onClick={(e) => { e.stopPropagation(); navigate("/performance"); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate("/performance"); } }}
          className="mt-2 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 cursor-pointer"
          style={{ background: "rgba(0,230,118,0.10)", border: "1px solid rgba(0,230,118,0.4)" }}
        >
          <ChartLineUp size={13} weight="fill" color="#00E676" className="shrink-0" />
          <span className="text-[10px] leading-tight text-white/70">
            <span className="font-bold text-perf-excellent">{t("matchsheet.hc_badge_title")}</span>
            {" · "}{t("matchsheet.hc_badge_stat", { pct: hc.accuracy_pct })}
          </span>
          <CaretRight size={11} weight="bold" color="#00E676" className="ml-auto shrink-0" />
        </span>
      )}
    </button>
  );
}

const SPORT_FEATURED_BG = {
  tennis: "https://images.pexels.com/photos/33114815/pexels-photo-33114815.jpeg",
  basket: "https://images.unsplash.com/photo-1773098654494-be1df2c16e86",
};

function matchDay(iso) {
  if (!iso) return "week";
  const d = new Date(iso);
  if (isNaN(d)) return "week";
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((day - start) / 86400000);
  if (diff <= 0) return "today";
  if (diff === 1) return "tomorrow";
  return "week";
}

function SportFeatured({ preds, sport }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!preds || !preds.length) return null;
  const p = [...preds].sort((a, b) => (b.recommendation?.prob || 0) - (a.recommendation?.prob || 0))[0];
  if (!p || !p.home || !p.away) return null;
  const c = bandColor(p.band);
  const kIso = p.kickoff || p.commence_time || null;
  const open = () => {
    if (p.match_id) navigate(`/match/${p.match_id}`, { state: { pred: p } });
    else navigate(`/prediction/${sport}/${encodeURIComponent(p.home.name)}/${encodeURIComponent(p.away.name)}`, { state: { pred: p } });
  };
  return (
    <div className="px-5">
      <div
        data-testid="sport-featured"
        onClick={open}
        className="relative rounded-2xl overflow-hidden cursor-pointer border border-white/10 fade-up"
      >
        <img src={SPORT_FEATURED_BG[sport]} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0A0A0A 12%, rgba(10,10,10,0.75) 55%, rgba(10,10,10,0.4))" }} />
        <div className="relative p-5 pt-6">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold">{t("home.featured_label", { competition: p.competition || "—" })}</span>
          <div className="flex items-center justify-center gap-6 my-5">
            <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
              <TeamBadge team={p.home} size={54} />
              <span className="text-sm font-bold text-center leading-tight">{p.home.name}</span>
              {p.home.rank ? <span className="text-[9px] font-bold text-gold/80">#{p.home.rank}</span> : null}
            </div>
            <div className="flex flex-col items-center shrink-0">
              <span className="font-numeral font-black text-2xl text-white/40">{t("home.vs")}</span>
              {kIso && (
                <span className="text-[11px] text-white/60 mt-1 text-center leading-tight">
                  {kickoffDate({ kickoff: kIso }, { weekday: "short", day: "2-digit", month: "short" })}
                  {kickoffTimeTz({ kickoff: kIso }) ? <> · {kickoffTimeTz({ kickoff: kIso })}</> : null}
                </span>
              )}
            </div>
            <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
              <TeamBadge team={p.away} size={54} />
              <span className="text-sm font-bold text-center leading-tight">{p.away.name}</span>
              {p.away.rank ? <span className="text-[9px] font-bold text-gold/80">#{p.away.rank}</span> : null}
            </div>
          </div>
          <div className="rounded-xl px-3 py-2 mb-4 flex items-center justify-between" style={{ background: `${c}14`, border: `1px solid ${c}44` }}>
            <span className="text-[12px] font-bold truncate" style={{ color: c }}>{p.recommendation?.text}</span>
            <span className="font-numeral font-black ml-2 shrink-0" style={{ color: c }}>{p.recommendation?.prob}%</span>
          </div>
          <button
            data-testid="sport-featured-cta"
            className="w-full h-11 rounded-xl bg-gold text-black font-bold text-sm flex items-center justify-center gap-2 glow-gold"
          >
            {t("home.analyze_match")} <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}


function C1Banner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/c1/status").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data?.open) return null;
  return (
    <div className="px-5 mt-4" data-testid="c1-banner">
      <div className="hm-card p-4" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(0,230,118,0.05))", border: "1px solid rgba(212,175,55,0.45)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={18} weight="fill" color="#D4AF37" />
          <span className="text-sm font-black text-gold truncate">{data.label} · {t("c1.open")}</span>
          <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse ml-auto shrink-0" />
        </div>
        <p className="text-[11px] text-white/55 mb-2">{t("c1.count", { count: data.count })}</p>
        <div className="space-y-1.5">
          {(data.fixtures || []).slice(0, 4).map((f, i) => {
            const clickable = !!f.match_id;
            return (
              <div
                key={i}
                data-testid={`c1-fixture-${i}`}
                onClick={clickable ? () => navigate(`/match/${f.match_id}`) : undefined}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === "Enter") navigate(`/match/${f.match_id}`); } : undefined}
                className={`flex items-center justify-between text-[12px] rounded-md -mx-1 px-1 py-0.5 ${clickable ? "cursor-pointer hover:bg-white/10 transition-colors" : ""}`}
              >
                <span className="text-white/85 font-semibold truncate">{f.home} <span className="text-white/30">vs</span> {f.away}</span>
                <span className="font-numeral text-white/45 text-[11px] shrink-0 ml-2">
                  {kickoffDate({ kickoff: f.commence_time }, { day: "2-digit", month: "short" })}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function HomeLoadError({ testid, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="mx-5 mb-2 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={testid}>
      <span className="text-[12px] text-white/60">{t("home.load_error")}</span>
      <button type="button" onClick={onRetry} data-testid={`${testid}-retry`}
        className="shrink-0 px-3 py-1 rounded-full border border-white/15 text-[11px] font-bold text-white/85 hover:bg-white/10 transition-colors">
        {t("home.retry")}
      </button>
    </div>
  );
}

function HomeEmpty({ testid }) {
  const { t } = useTranslation();
  return <p className="text-[12px] text-white/40 py-8 px-5" data-testid={testid}>{t("home.no_data")}</p>;
}

function FollowedTennisBlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const load = (signal) => api.get("/tennis/following", { signal })
    .then((r) => setMatches(r.data?.matches || []))
    .catch(() => {});
  usePolling(load, 30000, []);
  if (!matches.length) return null;
  const open = (m) => navigate(
    `/prediction/tennis/${encodeURIComponent(m.home)}/${encodeURIComponent(m.away)}`,
    { state: { pred: { home: { name: m.home, short: m.short.home, flag: m.flags.home },
      away: { name: m.away, short: m.short.away, flag: m.flags.away }, competition: m.tournament } } });
  const unfollow = async (m, e) => {
    e.stopPropagation();
    setMatches((prev) => prev.filter((x) => !(x.home === m.home && x.away === m.away)));
    try { await api.post("/tennis/unfollow", { home: m.home, away: m.away }); } catch { load(); }
  };
  return (
    <div className="mt-6" data-testid="followed-tennis-block">
      <div className="px-5 flex items-center gap-2 mb-3">
        <PushPin size={15} weight="fill" color="#D4AF37" />
        <h2 className="text-base font-bold tracking-tight">{t("live.followed")}</h2>
        <span className="text-[11px] text-white/40">{matches.length}</span>
      </div>
      <div className="px-5 space-y-2.5">
        {matches.map((m, i) => {
          const sc = m.score;
          const done = m.finished;
          return (
            <button key={i} data-testid={`followed-card-${i}`} onClick={() => open(m)}
              className="hm-card w-full p-3.5 text-left flex items-center gap-3"
              style={{ border: `1px solid ${done ? "rgba(255,255,255,0.08)" : "rgba(212,175,55,0.3)"}` }}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] font-bold uppercase tracking-widest truncate max-w-[60%]"
                    style={{ color: done ? "rgba(255,255,255,0.4)" : "#D4AF37" }}>{m.tournament || "Tennis"}</span>
                  <span className="text-[9px] font-bold" style={{ color: done ? "rgba(255,255,255,0.4)" : "#00E676" }}>
                    {done ? t("live.finished") : sc?.live ? t("live.badge") : ""}
                  </span>
                </div>
                {[{ n: m.home, s: m.short?.home, f: m.flags?.home, side: "home" },
                  { n: m.away, s: m.short?.away, f: m.flags?.away, side: "away" }].map((row) => (
                  <div key={row.side} className="flex items-center justify-between py-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {row.f && <img src={row.f} alt="" className="w-5 h-3.5 object-cover rounded-sm" />}
                      <span className={`text-[12px] truncate ${sc?.winner === row.side ? "font-black text-perf-excellent" : "font-semibold text-white/85"}`}>{row.n}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {(sc?.set_scores || []).map((s, j) => (
                        <span key={j} className="font-numeral font-bold text-[12px] w-3.5 text-center text-white/70">{s[row.side]}</span>
                      ))}
                      {sc?.live && sc?.game && (
                        <span className="font-numeral font-black text-[12px] w-5 text-center rounded px-0.5" style={{ background: "rgba(212,175,55,0.16)", color: "#D4AF37" }}>{sc.game[row.side]}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <span onClick={(e) => unfollow(m, e)} data-testid={`unfollow-${i}`}
                className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10">
                <X size={13} color="rgba(255,255,255,0.5)" />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LiveTennisBlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  usePolling((signal) => api.get("/tennis/live-matches", { signal })
    .then((r) => { if (!signal.aborted) setMatches(r.data?.matches || []); })
    .catch(() => {}), 25000, []);
  if (!matches.length) return null;
  const open = (m) => navigate(
    `/prediction/tennis/${encodeURIComponent(m.home.name)}/${encodeURIComponent(m.away.name)}`,
    { state: { pred: { home: m.home, away: m.away, competition: m.tournament, live: m } } });
  return (
    <div className="mt-6" data-testid="live-tennis-block">
      <div className="px-5 flex items-center gap-2 mb-3">
        <Broadcast size={16} weight="fill" color="#00E676" />
        <h2 className="text-base font-bold tracking-tight">{t("live.title")}</h2>
        <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />
        <span className="text-[11px] text-white/40">{matches.length}</span>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 snap-x pb-1">
        {matches.map((m, i) => (
          <button key={i} data-testid={`live-tennis-card-${i}`} onClick={() => open(m)}
            className="hm-card p-4 min-w-[230px] snap-start text-left"
            style={{ border: "1px solid rgba(0,230,118,0.35)", background: "rgba(0,230,118,0.05)" }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-perf-excellent truncate max-w-[70%]">{m.tournament}</span>
              <span className="text-[9px] font-bold text-white/40">{m.status}</span>
            </div>
            {[{ p: m.home, side: "home" }, { p: m.away, side: "away" }].map((row) => (
              <div key={row.side} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2 min-w-0">
                  <TeamBadge team={row.p} size={22} />
                  {m.serving === row.side && <TennisBall size={11} weight="fill" color="#D4AF37" />}
                  <span className={`text-[12px] truncate ${m.winner === row.side ? "font-black text-perf-excellent" : "font-semibold text-white/85"}`}>{row.p.name}</span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {(m.set_scores || []).map((s, j) => (
                    <span key={j} className="font-numeral font-bold text-[12px] w-3.5 text-center text-white/75">{s[row.side]}</span>
                  ))}
                  {m.game && <span className="font-numeral font-black text-[12px] w-5 text-center rounded px-0.5" style={{ background: "rgba(212,175,55,0.16)", color: "#D4AF37" }}>{m.game[row.side]}</span>}
                </div>
              </div>
            ))}
          </button>
        ))}
      </div>
    </div>
  );
}

function UpgradeBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const goPro = async () => {
    navigate("/paywall");
  };
  return (
    <div className="px-5 mt-6">
      <div
        data-testid="home-upgrade-banner"
        className="w-full rounded-2xl p-4 border border-gold/40"
        style={{ background: "linear-gradient(120deg, rgba(212,175,55,0.16), rgba(18,18,18,1))", boxShadow: "0 0 22px rgba(212,175,55,0.16)" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gold/18 border border-gold/50 shrink-0">
            <Gift size={20} weight="fill" color="#D4AF37" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold gold-text">{t("home.upgrade_title")}</p>
            <p className="text-[11px] text-white/60 leading-snug">{t("home.upgrade_desc")}</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button
            data-testid="home-trial-btn"
            onClick={goPro}
            disabled={loading}
            className="flex-1 h-10 rounded-xl bg-gold text-black text-sm font-bold glow-gold hover:bg-gold-dark transition-colors disabled:opacity-60"
          >
            {loading ? t("home.redirecting") : t("home.start_trial")}
          </button>
          <button
            data-testid="home-see-plans-btn"
            onClick={() => navigate("/passer-pro")}
            className="px-4 h-10 rounded-xl bg-white/8 text-white/80 text-sm font-semibold hover:bg-white/12 transition-colors"
          >
            {t("home.see_offers")}
          </button>
        </div>
      </div>
    </div>
  );
}

const COMPARE_KEYS = {
  feeling: ["home.feeling_1", "home.feeling_2", "home.feeling_3", "home.feeling_4", "home.feeling_5"],
  hitmax: ["home.hitmax_1", "home.hitmax_2", "home.hitmax_3", "home.hitmax_4", "home.hitmax_5"],
};

function CompareBlock() {
  const { t } = useTranslation();
  return (
    <Section title={t("home.compare_title")}>
      <div className="px-5 grid grid-cols-2 gap-3" data-testid="compare-block">
        <div className="rounded-2xl p-4 border border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center bg-perf-danger/15">
              <X size={15} weight="bold" className="text-perf-danger" />
            </span>
            <span className="text-sm font-bold text-white/70">{t("home.feeling")}</span>
          </div>
          <ul className="space-y-2.5">
            {COMPARE_KEYS.feeling.map((k, i) => (
              <li key={i} className="flex gap-2 text-xs text-white/50 leading-snug">
                <X size={14} weight="bold" className="mt-0.5 shrink-0 text-perf-danger" /><span>{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl p-4 border border-gold/40"
          style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.08), rgba(18,18,18,1))" }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center bg-gold/18">
              <Sparkle size={15} weight="fill" color="#D4AF37" />
            </span>
            <span className="text-sm font-bold gold-text">{t("home.with_hitmax")}</span>
          </div>
          <ul className="space-y-2.5">
            {COMPARE_KEYS.hitmax.map((k, i) => (
              <li key={i} className="flex gap-2 text-xs text-white/80 leading-snug">
                <Check size={14} weight="bold" className="mt-0.5 shrink-0 text-perf-excellent" /><span>{t(k)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

function ProofBlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [i, setI] = useState(0);
  useEffect(() => {
    api.get("/proof-samples").then((r) => setSamples(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (samples.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % samples.length), 3200);
    return () => clearInterval(t);
  }, [samples]);
  if (!samples.length) return null;
  const s = samples[i];
  return (
    <Section title={t("home.proof_title")}>
      <div className="px-5" data-testid="proof-block">
        <div className="hm-card p-5 overflow-hidden">
          <div key={i} className="fade-up">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/35 text-center mb-4">{s.date}</p>
            <div className="flex items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-1.5 w-20">
                <TeamBadge team={s.home} size={40} />
                <span className="text-xs font-bold w-full text-center leading-tight">{s.home.name}</span>
              </div>
              <span className="font-numeral font-black text-2xl text-gold">{s.score}</span>
              <div className="flex flex-col items-center gap-1.5 w-20">
                <TeamBadge team={s.away} size={40} />
                <span className="text-xs font-bold w-full text-center leading-tight">{s.away.name}</span>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 rounded-lg py-2 px-3"
              style={{ background: s.hit ? "rgba(0,230,118,0.12)" : "rgba(251,140,0,0.12)",
                border: `1px solid ${s.hit ? "rgba(0,230,118,0.4)" : "rgba(251,140,0,0.4)"}` }}>
              {s.hit
                ? <Check size={15} weight="bold" className="text-perf-excellent" />
                : <X size={15} weight="bold" className="text-perf-warning" />}
              <span className="text-[11px] font-semibold text-white/75">
                {t("home.proof_result", { model: s.model_favored, winner: s.winner })}
              </span>
            </div>
          </div>
          <div className="flex justify-center gap-1.5 mt-4">
            {samples.map((_, k) => (
              <span key={k} className={`h-1 rounded-full transition-all ${k === i ? "w-5 bg-gold" : "w-1.5 bg-white/20"}`} />
            ))}
          </div>
        </div>
        <p className="text-[10px] text-white/35 mt-2 text-center leading-snug">
          {t("home.proof_disclaimer")}
        </p>
      </div>
    </Section>
  );
}

function WinsWall() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [wins, setWins] = useState([]);
  const [stats, setStats] = useState({ confirmed_this_month: 0, current_streak: 0, total_confirmed: 0 });
  useEffect(() => {
    api.get("/performance/wins?limit=12").then((r) => {
      setWins(r.data.wins || []);
      if (r.data.stats) setStats(r.data.stats);
    }).catch(() => {});
  }, []);
  if (!wins.length) return null;
  return (
    <Section
      title={t("home.wins_title")}
      action={
        <button data-testid="wins-performance-link" onClick={() => navigate("/performance")}
          className="text-[11px] text-gold font-semibold flex items-center gap-1">
          {t("home.wins_see_all")} <CaretRight size={12} weight="bold" />
        </button>
      }
    >
      <p className="px-5 -mt-2 mb-3 text-[11px] text-white/40 leading-snug">{t("home.wins_sub")}</p>
      {stats.high_confidence && stats.high_confidence.sample >= 10 && stats.high_confidence.accuracy_pct != null && (
        <button data-testid="wins-hc-teaser" onClick={() => navigate("/performance")}
          className="mx-5 mb-3 flex items-center gap-3 rounded-2xl p-3.5 w-[calc(100%-2.5rem)] text-left"
          style={{ border: "1px solid rgba(0,230,118,0.4)", background: "rgba(0,230,118,0.06)" }}>
          <span className="font-numeral font-black text-3xl shrink-0" style={{ color: "#00E676" }}>{stats.high_confidence.accuracy_pct}%</span>
          <span className="flex-1 min-w-0">
            <span className="block text-xs font-semibold text-white/80">{t("home.wins_hc_title")}</span>
            <span className="block text-[10px] text-white/45">{t("home.wins_hc_sub", { count: stats.high_confidence.sample })}</span>
          </span>
          <span className="shrink-0 text-[11px] text-gold font-semibold flex items-center gap-1">
            {t("home.wins_hc_link")} <CaretRight size={12} weight="bold" />
          </span>
        </button>
      )}
      {(stats.confirmed_this_month > 0 || stats.current_streak >= 3) && (
        <div className="px-5 mb-3 flex flex-wrap items-center gap-2" data-testid="wins-stats">
          {stats.confirmed_this_month > 0 && (
            <span data-testid="wins-month-count"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold"
              style={{ background: "rgba(0,230,118,0.12)", border: "1px solid rgba(0,230,118,0.4)", color: "#00E676" }}>
              <Check size={13} weight="bold" /> {t("home.wins_month_count", { count: stats.confirmed_this_month })}
            </span>
          )}
          {stats.current_streak >= 3 && (
            <span data-testid="wins-streak-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black"
              style={{ background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.5)", color: "#D4AF37" }}>
              <Fire size={13} weight="fill" /> {t("home.wins_streak", { count: stats.current_streak })}
            </span>
          )}
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 snap-x pb-1" data-testid="wins-wall">
        {wins.map((w) => (
          <button key={w.match_id} data-testid={`win-card-${w.match_id}`}
            onClick={() => navigate(`/match/${w.match_id}`)}
            className="hm-card p-4 min-w-[220px] snap-start text-left"
            style={{ border: "1px solid rgba(0,230,118,0.35)", background: "rgba(0,230,118,0.04)" }}>
            <div className="flex items-center gap-1.5 mb-3">
              <Check size={13} weight="bold" className="text-perf-excellent" />
              <span className="text-[9px] font-black uppercase tracking-widest text-perf-excellent">{t("home.wins_badge")}</span>
              {w.hitmax_score != null && <span className="ml-auto font-numeral font-black text-sm text-perf-excellent">{w.hitmax_score}</span>}
            </div>
            <div className="flex items-center justify-center gap-3">
              <TeamBadge team={{ crest: w.home_crest, name: w.home }} size={30} />
              {w.score ? <span className="font-numeral font-black text-lg">{w.score}</span> : <span className="text-white/30 text-sm font-bold">VS</span>}
              <TeamBadge team={{ crest: w.away_crest, name: w.away }} size={30} />
            </div>
            <div className="flex items-center justify-between mt-2 gap-2 text-[11px]">
              <span className="truncate font-semibold text-white/70 flex-1">{w.home}</span>
              <span className="truncate font-semibold text-white/70 text-right flex-1">{w.away}</span>
            </div>
            {w.recommendation_text && (
              <div className="mt-2 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-perf-excellent truncate" style={{ background: "rgba(0,230,118,0.12)", border: "1px solid rgba(0,230,118,0.35)" }}>
                ✓ {w.recommendation_text}
              </div>
            )}
          </button>
        ))}
      </div>
    </Section>
  );
}

function LiveOddsBlock() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/odds/live-fixtures").then((r) => setData(r.data)).catch(() => setData({ available: false }));
  }, []);
  if (!data || !data.available || !data.fixtures?.length) return null;
  const pill = (label, okey, val, on) => {
    const c = outcomeColor(okey);
    return (
      <div className="flex-1 rounded-lg py-1.5 text-center bg-white/5" style={on ? { border: `1px solid ${c}`, background: `${c}1A` } : {}}>
        <p className="text-[9px] uppercase" style={{ color: c }}>{label}</p>
        <p className="font-numeral font-bold text-sm" style={{ color: c }}>{val?.toFixed(2)}</p>
      </div>
    );
  };
  return (
    <Section title={t("home.live_odds_title")}>
      <div className="px-5 flex items-center gap-1.5 -mt-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />
        <span className="text-[11px] text-white/45">{t("home.live_odds_sub")}</span>
      </div>
      <div className="px-5 space-y-3" data-testid="live-odds-block">
        {data.fixtures.slice(0, 6).map((f) => {
          const clickable = !!f.match_id;
          return (
            <div
              key={f.id}
              data-testid={`live-odds-card-${f.id}`}
              onClick={clickable ? () => navigate(`/match/${f.match_id}`) : undefined}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === "Enter") navigate(`/match/${f.match_id}`); } : undefined}
              className={`hm-card p-4 ${clickable ? "cursor-pointer hover:border-gold/40 transition-colors" : ""}`}
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-sm font-bold truncate">{f.home}</span>
                <span className="text-[10px] text-white/40 px-2">{t("home.vs_lower")}</span>
                <span className="text-sm font-bold truncate text-right">{f.away}</span>
              </div>
              <div className="flex gap-2">
                {pill("1", "home", f.odds.home, f.favorite === "home")}
                {pill("N", "draw", f.odds.draw, f.favorite === "draw")}
                {pill("2", "away", f.odds.away, f.favorite === "away")}
              </div>
              <p className="text-[10px] text-white/35 mt-2">
                {t("home.implied_probs", { home: f.implied.home, draw: f.implied.draw, away: f.implied.away, bookmakers: f.bookmakers })}
              </p>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function ConfidenceGauge({ n }) {
  const { t } = useTranslation();
  const games = n || 0;
  let level, color, pct;
  if (games < 10) { level = t("home.conf_low"); color = "#EF4444"; pct = 33; }
  else if (games <= 15) { level = t("home.conf_mid"); color = "#F59E0B"; pct = 66; }
  else { level = t("home.conf_high"); color = "#00E676"; pct = 100; }
  return (
    <div className="flex items-center gap-2 flex-1 min-w-0" data-testid="confidence-gauge" data-confidence-level={level}>
      <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden min-w-[40px]">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 6px ${color}` }} />
      </div>
      <span className="text-[10px] font-bold whitespace-nowrap" style={{ color }}>{t("home.confidence", { level })}</span>
    </div>
  );
}


export function ValueOfDayBlock({ user, preview }) {
  if (!isValueEnabled()) return null;  // fonctionnalité « Value » retirée
  return <SportValueBlock sport="foot" user={user} preview={preview} />;
}

function SportValueBlock({ sport, user, preview }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hc = useHighConfidence();
  const [data, setData] = useState(null);
  const planRef = useRef(user?.plan);
  const [league, setLeague] = useState("all");
  const [market, setMarket] = useState("all");
  const [edgeMin, setEdgeMin] = useState(0);
  const isFoot = sport === "foot";
  const endpoint = isFoot ? "/value-of-the-day" : `/${sport}/value-of-the-day`;
  const historyRoute = isFoot ? "/value-history" : `/${sport}-value-history`;
  useEffect(() => {
    // Plan changé (achat, expiration) : on ne réutilise pas la réponse partagée de l'ancien plan.
    if (planRef.current !== user?.plan) {
      planRef.current = user?.plan;
      if (isFoot) resetValueOfDay();
    }
    (isFoot ? loadValueOfDay() : api.get(endpoint).then((r) => r.data))
      .then(setData).catch(() => setData({ available: false }));
  }, [endpoint, isFoot, user?.plan]);
  if (!data || !data.available || !data.items?.length) return null;
  const pro = data.pro;
  const leagues = Array.from(new Set(data.items.map((i) => i.league)));
  const MARKETS = [
    { key: "all", label: t("home.market_all") },
    { key: "home", label: t("home.market_home") },
    { key: "draw", label: t("home.market_draw") },
    { key: "away", label: t("home.market_away") },
  ];
  const EDGES = [
    { v: 0, label: t("home.edge_all") },
    { v: 5, label: t("home.edge_5") },
    { v: 8, label: t("home.edge_8") },
    { v: 10, label: t("home.edge_10") },
  ];
  let items = league === "all" ? data.items : data.items.filter((i) => i.league === league);
  if (isFoot) items = market === "all" ? items : items.filter((i) => i.best?.key === market);
  if (pro && edgeMin > 0) items = items.filter((i) => (i.best?.edge ?? -99) >= edgeMin);
  const shown = preview ? items.slice(0, 3) : items;
  const hcReady = hc && hc.sample >= 10 && hc.accuracy_pct != null;
  const title = isFoot ? t("home.value_of_day") : t(`value.${sport}_title`);
  const sub = isFoot ? t("home.value_sub") : t(`value.${sport}_sub`);
  const open = (r) => isFoot
    ? navigate(`/vf/${r.fixture_id}`)
    : navigate(`/prediction/${sport}/${encodeURIComponent(r.home.name)}/${encodeURIComponent(r.away.name)}`,
        { state: { pred: { ...r, competition: r.league } } });
  return (
    <Section title={title}>
      <div className="px-5 flex items-center justify-between -mt-2 mb-3">
        <div className="flex items-center gap-1.5">
          <Scales size={13} weight="fill" color="#D4AF37" />
          <span className="text-[11px] text-white/45">{sub}</span>
        </div>
        {preview ? (
          <button data-testid="value-see-all-link" onClick={() => navigate("/value")}
            className="text-[11px] text-gold font-semibold flex items-center gap-1">
            {t("home.value_see_all")} <CaretRight size={11} weight="bold" />
          </button>
        ) : (
          <button data-testid={isFoot ? "value-history-link" : `${sport}-value-history-link`} onClick={() => navigate(historyRoute)}
            className="text-[11px] text-gold font-semibold">{t("home.history")}</button>
        )}
      </div>
      {!preview && leagues.length > 1 && (
        <div className="px-5 flex gap-1.5 overflow-x-auto no-scrollbar mb-2" data-testid="league-tabs">
          {["all", ...leagues].map((l) => (
            <button key={l} data-testid={`league-tab-${l}`} onClick={() => setLeague(l)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${league === l ? "bg-gold text-black border-gold" : "border-white/10 text-white/55"}`}>
              {l === "all" ? t("home.all") : l}
            </button>
          ))}
        </div>
      )}
      {!preview && isFoot && (
        <div className="px-5 flex gap-1.5 overflow-x-auto no-scrollbar mb-3" data-testid="market-tabs">
          {MARKETS.map((m) => (
            <button key={m.key} data-testid={`market-tab-${m.key}`} onClick={() => setMarket(m.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${market === m.key ? "bg-white text-black border-white" : "border-white/10 text-white/45"}`}>
              {m.label}
            </button>
          ))}
        </div>
      )}
      {!preview && pro && (
        <div className="px-5 flex gap-1.5 overflow-x-auto no-scrollbar mb-3" data-testid="edge-tabs">
          {EDGES.map((e) => (
            <button key={e.v} data-testid={`edge-tab-${e.v}`} onClick={() => setEdgeMin(e.v)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${edgeMin === e.v ? "bg-perf-excellent/20 text-perf-excellent border-perf-excellent/50" : "border-white/10 text-white/45"}`}>
              {e.label}
            </button>
          ))}
        </div>
      )}
      <div className="px-5 space-y-3" data-testid={isFoot ? "value-of-day-block" : `${sport}-value-block`}>
        {shown.length === 0 && (
          <p className="text-center text-[12px] text-white/35 py-6" data-testid="vod-empty">{t("home.no_value")}</p>
        )}
        {shown.map((r, i) => {
          const b = r.best;
          const detected = b.edge != null && b.edge >= 5;
          return (
            <div key={r.fixture_id || r.key || i} onClick={() => open(r)}
              className="hm-card p-4 cursor-pointer hover:border-gold/30 transition-colors relative"
              data-testid={isFoot ? "vod-item" : `${sport}-value-item-${i}`}>
              {r.is_new && (
                <div className="flex justify-center mb-2.5">
                  <span data-testid="vod-new-badge" className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-perf-excellent/15 border border-perf-excellent/40 text-[9px] font-extrabold text-perf-excellent uppercase tracking-wider">
                    <span className="w-1 h-1 rounded-full bg-perf-excellent animate-pulse" /> {t("home.new")}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <TeamBadge team={isFoot ? { ...r.home, color: "#43D65A" } : r.home} size={26} />
                  <span className="text-sm font-bold truncate">{r.home.name}</span>
                </div>
                <span className="text-[10px] text-white/35">vs</span>
                <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                  <span className="text-sm font-bold truncate">{r.away.name}</span>
                  <TeamBadge team={isFoot ? { ...r.away, color: "#43D65A" } : r.away} size={26} />
                </div>
              </div>
              <p className="text-[10px] text-white/30 mt-1 text-center">{r.league}</p>
              {(r.date || r.commence_time || r.kickoff) && (
                <p className="text-[11px] text-white/60 font-semibold text-center mt-1" data-testid={`vod-kickoff-${r.fixture_id || i}`}>
                  {kickoffDate({ kickoff: r.date || r.commence_time || r.kickoff }, { weekday: "short", day: "2-digit", month: "short" })} · {kickoffTimeTz({ kickoff: r.date || r.commence_time || r.kickoff })}
                </p>
              )}
              {pro ? (
                <>
                  <div className="mt-3 flex items-center justify-between rounded-lg px-3 py-2"
                    style={{ background: detected ? "rgba(0,230,118,0.12)" : "rgba(255,255,255,0.04)",
                      border: `1px solid ${detected ? "rgba(0,230,118,0.4)" : "rgba(255,255,255,0.08)"}` }}>
                    <span className="text-xs text-white/75">
                      {detected ? t("home.value_prefix") : t("home.gap_prefix")}<b style={{ color: outcomeColor(b.key) }}>{b.label}</b> · {t("home.model_vs_market", { model: b.model_pct, market: b.implied_pct })}
                    </span>
                    <span className={`font-numeral font-black text-sm whitespace-nowrap shrink-0 ml-2 ${detected ? "text-perf-excellent" : "text-white/50"}`}>
                      {b.edge >= 0 ? "+" : ""}{b.edge} pts
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    {r.confidence != null ? <ConfidenceGauge n={r.confidence} /> : <span />}
                    <span className="text-[10px] text-white/30 whitespace-nowrap">{t("home.bookmakers", { count: r.bookmakers })}</span>
                  </div>
                </>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); navigate("/passer-pro"); }}
                  data-testid={isFoot ? "vod-locked-cta" : `${sport}-value-locked-cta`}
                  className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 border border-gold/40 bg-gold/10 text-gold text-xs font-bold">
                  <LockKey size={14} weight="fill" /> {t("home.unlock_value", { label: b.label })}
                </button>
              )}
              {isFoot && r.band === "excellent" && hcReady && (
                <span role="button" tabIndex={0}
                  data-testid={`vod-hc-badge-${r.fixture_id || i}`}
                  onClick={(e) => { e.stopPropagation(); navigate("/performance"); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); navigate("/performance"); } }}
                  className="mt-2 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 cursor-pointer"
                  style={{ background: "rgba(0,230,118,0.10)", border: "1px solid rgba(0,230,118,0.4)" }}>
                  <ChartLineUp size={13} weight="fill" color="#00E676" className="shrink-0" />
                  <span className="text-[10px] leading-tight text-white/70">
                    <span className="font-bold text-perf-excellent">{t("matchsheet.hc_badge_title")}</span>
                    {" · "}{t("matchsheet.hc_badge_stat", { pct: hc.accuracy_pct })}
                  </span>
                  <CaretRight size={11} weight="bold" color="#00E676" className="ml-auto shrink-0" />
                </span>
              )}
            </div>
          );
        })}
        {preview && items.length > shown.length && (
          <button data-testid="value-see-all-btn" onClick={() => navigate("/value")}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 border border-gold/30 text-gold text-xs font-bold hover:bg-gold/10 transition-colors">
            {t("home.value_see_all_count", { count: items.length })} <CaretRight size={12} weight="bold" />
          </button>
        )}
      </div>
    </Section>
  );
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const i18nLocale = { fr: "fr-FR", en: "en-GB", es: "es-ES" }[i18n.language?.slice(0, 2)] || "fr-FR";
  const { user } = useAuth();
  const { sport, setSport, meta } = useSport();

  // Sport favori choisi à l'inscription = sport par défaut de l'accueil, tant
  // que l'utilisateur n'a pas manuellement changé d'onglet (respecte son choix).
  useEffect(() => {
    const fs = user?.favorite_sport;
    if (fs && !localStorage.getItem("hitmax_sport_touched") && fs !== sport) {
      setSport(fs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.favorite_sport]);
  const [featured, setFeatured] = useState(null);
  const [matches, setMatches] = useState([]);
  const [demo, setDemo] = useState(false);
  const [trends, setTrends] = useState([]);
  const [preds, setPreds] = useState([]);
  const [predsDemo, setPredsDemo] = useState(false);
  const [tFilter, setTFilter] = useState("all");
  const [tTour, setTTour] = useState("all");
  const [dayFilter, setDayFilter] = useState("today");
  const [hcMatchOnly, setHcMatchOnly] = useState(false);
  const hcStat = useHighConfidence();

  // 3 états par bloc : "loading" / "ok" (données ou aucune donnée) / "error" (dernières données conservées).
  const [st, setSt] = useState({ preds: "loading", featured: "loading", matches: "loading" });
  const mark = (k, v) => setSt((x) => ({ ...x, [k]: v }));
  const loadHome = React.useCallback(() => {
    api.get(`/ext/predictions/${sport}`).then((r) => { setPreds(r.data.predictions || []); setPredsDemo(r.data.demo); mark("preds", "ok"); })
      .catch(() => mark("preds", "error"));
    if (sport !== "foot") return;
    api.get("/matches/featured").then((r) => { setFeatured(r.data); mark("featured", "ok"); }).catch(() => mark("featured", "error"));
    api.get("/live/matches").then((r) => { setMatches(r.data?.matches || []); setDemo(!!r.data?.demo); mark("matches", "ok"); })
      .catch(() => mark("matches", "error"));
    api.get("/trends").then((r) => setTrends(Array.isArray(r.data) ? r.data : [])).catch(() => setTrends([]));
  }, [sport]);
  useEffect(() => {
    setSt({ preds: "loading", featured: "loading", matches: "loading" });
    loadHome();
    window.addEventListener("online", loadHome); // relance automatique au retour du réseau
    return () => window.removeEventListener("online", loadHome);
  }, [loadHome]);

  const favTeams = (user?.favorites || []).filter((f) => f.kind === "team").map((f) => f.ref_id);
  // Les favoris viennent de db.teams (id "af_541") mais /live/matches renvoie un
  // team_id brut ("541"). On normalise pour que la priorisation fonctionne.
  const favIds = new Set(favTeams.map((x) => String(x).replace(/^af_/, "")));
  const isFavMatch = (mm) => favIds.has(String(mm.home?.team_id)) || favIds.has(String(mm.away?.team_id));
  // Priorise les matchs de l'équipe favorite en tête (données réelles ET démo),
  // sans jamais masquer le reste (tri stable, on ne filtre pas).
  const sortedMatches = favIds.size
    ? [...matches].sort((a, b) => (isFavMatch(b) ? 1 : 0) - (isFavMatch(a) ? 1 : 0))
    : matches;
  const hasExcellentMatch = sortedMatches.some((m) => m.band === "excellent");
  const visibleMatches = hcMatchOnly ? sortedMatches.filter((m) => m.band === "excellent") : sortedMatches;

  return (
    <>
      <Header />
      <div className="pt-2">
        {user?.name && (
          <p className="px-5 mt-4 text-sm text-white/50">
            <Trans i18nKey="home.greeting" values={{ name: user.name }} components={{ 1: <span className="text-white font-semibold" /> }} />
          </p>
        )}

        {!isPro(user) && <UpgradeBanner />}

        <LaunchCountdown />

        <LotteryBanner />

        {sport === "foot" && <C1Banner />}

        {sport !== "foot" ? (
          <>
            <Section title={t("home.featured")}>
              {preds.length ? <SportFeatured preds={preds} sport={sport} /> : <div className="mx-5 h-64 rounded-2xl skeleton" />}
            </Section>
            {sport === "tennis" && <FollowedTennisBlock />}
            {sport === "tennis" && <LiveTennisBlock />}
            {(sport === "basket" || sport === "tennis") && isValueEnabled() && <SportValueBlock sport={sport} user={user} />}
            <Section
              title={t("home.preds_label", { sport: t(`sports.${sport}`) })}
              action={
                <span data-testid="preds-source-badge"
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1.5 ${
                    predsDemo ? "bg-white/5 text-white/40" : "bg-perf-excellent/15 text-perf-excellent"
                  }`}>
                  {!predsDemo && <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />}
                  {predsDemo ? t("home.demo") : t("home.live")}
                </span>
              }
            >
              {sport === "tennis" && preds.length > 0 && (
                <div className="px-5 flex gap-1.5 overflow-x-auto no-scrollbar mb-3" data-testid="tennis-filter-tabs">
                  {[
                    { k: "all", label: t("tennisfilter.all") },
                    { k: "men", label: t("tennisfilter.men") },
                    { k: "women", label: t("tennisfilter.women") },
                    { k: "slam", label: t("tennisfilter.slam") },
                  ].map((f) => (
                    <button key={f.k} data-testid={`tennis-filter-${f.k}`} onClick={() => setTFilter(f.k)}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${tFilter === f.k ? "bg-gold text-black border-gold" : "border-white/10 text-white/55"}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
              {sport === "tennis" && preds.length > 0 && (() => {
                const tournaments = Array.from(new Set(preds.map((p) => p.competition).filter(Boolean)));
                if (tournaments.length < 2) return null;
                return (
                  <div className="px-5 flex gap-1.5 overflow-x-auto no-scrollbar mb-3" data-testid="tennis-tournament-tabs">
                    {["all", ...tournaments].map((tn) => (
                      <button key={tn} data-testid={`tennis-tournament-${tn === "all" ? "all" : "item"}`} onClick={() => setTTour(tn)}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border transition-colors ${tTour === tn ? "bg-white text-black border-white" : "border-white/10 text-white/45"}`}>
                        {tn === "all" ? t("tennisfilter.all_tournaments") : tn}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {st.preds === "error" && <HomeLoadError testid={`preds-${sport}-error`} onRetry={loadHome} />}
              <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 snap-x pb-1" data-testid={`preds-${sport}`}>
                {(() => {
                  let list = sport === "tennis" ? preds.filter((p) =>
                    tFilter === "all" ? true
                    : tFilter === "slam" ? p.grand_slam
                    : tFilter === "men" ? p.gender === "men"
                    : tFilter === "women" ? p.gender === "women" : true) : preds;
                  if (sport === "tennis" && tTour !== "all") list = list.filter((p) => p.competition === tTour);
                  if (!preds.length) return st.preds === "loading"
                    ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="min-w-[250px] h-40 rounded-2xl skeleton" />)
                    : st.preds === "ok" ? <HomeEmpty testid={`preds-${sport}-empty`} /> : null;
                  if (!list.length) return <p className="text-[12px] text-white/40 py-8 px-2" data-testid="tennis-filter-empty">{t("tennisfilter.empty")}</p>;
                  return list.map((p, i) => <PredCard key={i} p={p} sport={sport} pro={isPro(user)} />);
                })()}
              </div>
            </Section>
            <div className="px-5 mt-1">
              <p className="text-[11px] text-white/40 leading-snug">
                {t("home.other_sport_note", { sport: t(`sports.${sport}`) })}
              </p>
            </div>
          </>
        ) : (
          <>
            <Section title={t("home.featured")}>
              {st.featured === "error" && <HomeLoadError testid="featured-error" onRetry={loadHome} />}
              {featured ? <FeaturedMatch m={featured} />
                : st.featured === "loading" ? <div className="mx-5 h-72 rounded-2xl skeleton" />
                : st.featured === "ok" ? <HomeEmpty testid="featured-empty" /> : null}
            </Section>

            <Section
              title={t("home.matches_today")}
              action={
                <div className="flex items-center gap-2">
                  <button
                    data-testid="followed-matches-btn"
                    onClick={() => navigate("/mes-matchs")}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 text-white/70 hover:text-white text-[10px] font-bold transition-colors"
                  >
                    <Bookmarks size={12} weight="fill" /> Suivis
                  </button>
                  {hasExcellentMatch && hcStat && hcStat.sample >= 10 && (
                    <button data-testid="hc-match-filter" onClick={() => setHcMatchOnly((v) => !v)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 border transition-colors ${hcMatchOnly ? "bg-perf-excellent/20 text-perf-excellent border-perf-excellent/50" : "border-white/10 text-white/45"}`}>
                      <ChartLineUp size={11} weight="fill" /> {t("home.hc_filter")}
                    </button>
                  )}
                  <span
                    data-testid="matches-source-badge"
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1.5 ${
                      demo ? "bg-white/5 text-white/40" : "bg-perf-excellent/15 text-perf-excellent"
                    }`}
                  >
                    {!demo && <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />}
                    {demo ? t("home.demo") : t("home.live")}
                  </span>
                </div>
              }
            >
              {st.matches === "error" && <HomeLoadError testid="matches-error" onRetry={loadHome} />}
              {(() => {
                const dayCounts = {
                  today: visibleMatches.filter((m) => matchDay(m.kickoff) === "today").length,
                  tomorrow: visibleMatches.filter((m) => matchDay(m.kickoff) === "tomorrow").length,
                  week: visibleMatches.length,
                };
                const dayTabs = [
                  { k: "today", label: "Aujourd'hui" },
                  { k: "tomorrow", label: "Demain" },
                  { k: "week", label: "Cette semaine" },
                ];
                const shown = dayFilter === "week" ? visibleMatches : visibleMatches.filter((m) => matchDay(m.kickoff) === dayFilter);
                return (
                  <>
                    {!demo && sortedMatches.length > 0 && (
                      <div className="flex gap-2 px-5 mb-3 overflow-x-auto no-scrollbar" data-testid="day-filter-tabs">
                        {dayTabs.map((tab) => {
                          const active = dayFilter === tab.k;
                          return (
                            <button
                              key={tab.k}
                              data-testid={`day-tab-${tab.k}`}
                              onClick={() => setDayFilter(tab.k)}
                              className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors flex items-center gap-1.5 ${
                                active ? "bg-perf-excellent text-black" : "bg-white/5 text-white/60 hover:text-white"
                              }`}
                            >
                              {tab.label}
                              <span className={active ? "text-black/60" : "text-white/40"}>{dayCounts[tab.k]}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 snap-x pb-1">
                      {shown.length ? (
                        shown.map((m) => <MatchCard key={m.match_id} match={m} />)
                      ) : sortedMatches.length ? (
                        <p className="text-[12px] text-white/40 py-8 px-2" data-testid="day-filter-empty">
                          {hcMatchOnly ? t("home.no_value") : "Aucun match pour ce jour · essaie « Cette semaine »"}
                        </p>
                      ) : st.matches === "loading" ? (
                        Array.from({ length: 3 }).map((_, i) => <div key={i} className="min-w-[240px] h-40 rounded-2xl skeleton" />)
                      ) : st.matches === "ok" ? (
                        <HomeEmpty testid="matches-empty" />
                      ) : null}
                    </div>
                  </>
                );
              })()}
            </Section>

            <ValueOfDayBlock user={user} preview />
            <LiveOddsBlock />
            <div className="px-5 mt-1 mb-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="home-quick-access">
              {[
                { key: "performance", label: t("home.quick_performance"), to: "/performance" },
                { key: "matchs", label: t("home.quick_matches"), to: "/mes-matchs" },
                { key: "stats", label: t("home.quick_stats"), to: "/stats" },
                { key: "play", label: t("home.quick_play"), to: "/play" },
              ].map((q) => (
                <button key={q.key} data-testid={`quick-${q.key}`} onClick={() => navigate(q.to)}
                  className="px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap border border-white/10 text-white/70 hover:border-gold/50 hover:text-gold transition-colors shrink-0">
                  {q.label}
                </button>
              ))}
            </div>

            <Section
              title={t("home.upcoming_preds")}
              action={
                <span data-testid="preds-source-badge"
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1.5 ${
                    predsDemo ? "bg-white/5 text-white/40" : "bg-perf-excellent/15 text-perf-excellent"
                  }`}>
                  {!predsDemo && <span className="w-1.5 h-1.5 rounded-full bg-perf-excellent animate-pulse" />}
                  {predsDemo ? t("home.demo") : t("home.live")}
                </span>
              }
            >
              {(() => {
                const raw = preds[0]?.date || preds[0]?.kickoff || preds[0]?.datetime || preds[0]?.match_date || preds[0]?.start;
                const d = raw ? new Date(raw) : new Date();
                const txt = isNaN(d) ? "" : d.toLocaleDateString(i18nLocale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
                return txt ? (
                  <div className="text-center text-[12px] font-bold uppercase tracking-[0.22em] text-gold mb-3" data-testid="preds-date">
                    {txt}
                  </div>
                ) : null;
              })()}
              {st.preds === "error" && <HomeLoadError testid="preds-foot-error" onRetry={loadHome} />}
              <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 snap-x pb-1">
                {preds.length ? preds.map((p, i) => <PredCard key={i} p={p} sport={sport} pro={isPro(user)} />)
                  : st.preds === "loading" ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="min-w-[250px] h-40 rounded-2xl skeleton" />)
                  : st.preds === "ok" ? <HomeEmpty testid="preds-foot-empty" /> : null}
              </div>
            </Section>

            <Section title={t("home.trends_today")}>
              <div className="flex gap-3 overflow-x-auto no-scrollbar px-5 snap-x pb-1">
                {(trends || []).map((t) => <TrendCard key={t.trend_id} t={t} />)}
              </div>
            </Section>
          </>
        )}

        {sport === "foot" && (
          <>
            {!user && <CompareBlock />}
            <WinsWall />
            <ProofBlock />
          </>
        )}
        <Footer />
      </div>
    </>
  );
}
